import { defineStore } from 'pinia';
import type { PageConfig } from '@mts/render';
import type { RouteLocationNormalized } from 'vue-router';
import {
  $MTS,
  createDayTimeChecker,
  message,
  pick,
  recursiveTimeout,
  UserInteractionDetector,
} from '@mts-fe/utils';
import { useWS } from '@mts/hooks';
import { t } from '@mts/hooks/web/useI18n';
import { warn } from '@mts/utils';
import { IN_DEV, PADMode } from '@mts/utils/env';
import {
  bindAvatarStation,
  createApi,
  getSampleStationInfo,
  getSortStationInfo,
  getStationInfo,
  getWorkStationInfo,
  unbindAvatarStation,
  updateSampleStationInfo,
  updateSortStationInfo,
  updateStationInfo,
} from '@/api';
import AUTH_API from '@/api/ims-auth';
import { default as API, parseConfigValue, SysConfig, SysConfigType } from '@/api/ims-system';
import { getPermissionStore, getUserStore } from '@/store';

export enum StationMode {
  STA = 'stationInfo',
  WK_STA = 'workStation',
  SST = 'sortationStation',
  YYBD = 'sampleStation',
  AVATAR_STA = 'avatarStation',
}

interface StationInfo {
  lineNo: string;
  stationNo: string;
  factoryNo?: string;
}
interface DebugInfo {
  routePage: { name?: string; fixName?: string; filePath?: string; fileName?: string };
  configList: {
    pageCode: string;
    pageTitle: string;
    template?: string;
    dataStatus?: 1 | 2;
    templateList?: Recordable[];
    parentCode?: string;
  }[];
}
interface SewingConfig {
  inTransitOccupancyRateThreshold: number;
  stayAlert: number;
  sewingRouteAssignToPeople: number;
  emptyRackBackMode: number;
  stationHangerAverageDistributionThreshold: number;
  sewingInTransitOccupancyRateThreshold: number;
  upperCuttingEmployeeIncreaseCntLimit: number;
  upperCuttingEmployeeIncreaseCntLimitSwitch: boolean;
  rackStopThresholdMinutes: number;
}

export const useGlobalStore = defineStore('app-global-config', {
  persist: {
    omit: [
      'disableRouteTransition',
      'globalRefs',
      'exportModal',
      'userConfg',
      'fetchTimers',
      'sendMessageModal',
    ],
  },
  state: () => ({
    routerViewKey: Date.now().toString(),
    disableRouteTransition: false,
    globalRefs: {} as Recordable,
    exportModal: { open: false, typeCode: '' },
    sendMessageModal: { open: false, options: {} as Record<string, any> },
    stationMode:
      (($MTS.App.prop('STATION_MODE') || $MTS.App.getItem('STATION_MODE')) as StationMode) ||
      StationMode.STA,
    debugInfo: { routePage: {}, configList: [] } as DebugInfo,
    useRecord: {} as Recordable,
    systemConfg: {} as Recordable,
    userConfg: {} as Recordable,
    sewingConfig: {} as SewingConfig,
    fetchTimers: {} as Recordable<{ stop: () => void }>,
    detector: {} as UserInteractionDetector,
  }),
  getters: {
    getConfig: (state) => ({ ...state.systemConfg, ...state.userConfg }),
    getSampleFactoryNo(): string {
      return this.getConfig['sample_garment_sorting_config']?.factory;
    },
    getFactoryNo(): string {
      return this.stationMode === StationMode.YYBD
        ? this.getSampleFactoryNo
        : this.getConfig['suspension_config']?.factory;
    },
    getStationInfo(): (StationInfo & { lineStation: string }) | undefined {
      const info = this.useRecord[this.stationMode] as StationInfo | undefined;
      if (!info) return info;

      const factoryNo = info.factoryNo || this.getFactoryNo;
      const lineStation = `${info.lineNo || ''}-${info.stationNo || ''}`;
      return { ...info, factoryNo, lineStation };
    },
    getStationKey(): string {
      const stationInfo = this.getStationInfo;
      if (!stationInfo?.factoryNo || stationInfo.lineStation === '-') return '';

      const { lineNo, stationNo, factoryNo } = stationInfo;
      return [lineNo, stationNo, factoryNo].filter(Boolean).join('_');
    },
    getWorkStationInfo(): Recordable | undefined {
      return this.useRecord[StationMode.WK_STA];
    },
    getSortStationInfo(): Recordable | undefined {
      return this.useRecord[StationMode.SST];
    },
    /** 分身绑定信息（服务端无查询接口，依赖本地持久化展示） */
    getAvatarStationInfo(): Recordable | undefined {
      return this.useRecord[StationMode.AVATAR_STA];
    },
    /** 本机是否存在生效中的分身绑定（true 时全局请求附加 X-Station-Avatar 头） */
    getIsAvatarBound(): boolean {
      return !!this.useRecord[StationMode.AVATAR_STA]?.stationNo;
    },
    getDeveloperMode(): boolean {
      return !!this.useRecord['developerMode'];
    },
    /** 获取当前是否是验厂隐藏模式 */
    getCheckHidden(): boolean {
      return this.systemConfg['production:report_mode'] === 1;
    },
    /** 用户设备震动强度 */
    getDiviceIntensity(): number {
      return this.getConfig['user_divice_intensity'] || 20;
    },
    /** 用户设备灵敏度 */
    getDiviceDelay(): number {
      return this.getConfig['user_divice_delay'] || 600;
    },
  },
  actions: {
    setGlobalRefs(refs: Recordable) {
      Object.assign(this.globalRefs, refs);
    },
    updateExportModal(open = false, typeCode = '') {
      this.exportModal = { open, typeCode };
    },
    updateSendMessageModal(open = false, options: Recordable = {}) {
      this.sendMessageModal = { open, options };
    },
    forceReloadView(key = Date.now().toString()) {
      this.routerViewKey = key;
    },
    cacheRoutePage(route: RouteLocationNormalized) {
      const { configPath } = route.meta || {};
      // 获取当前路由的组件信息
      let routePage: DebugInfo['routePage'] | null = null;
      for (const matched of route.matched) {
        if (matched.redirect) continue;

        // @ts-ignore
        const { name, __file } = matched.components?.default || {};
        if (!__file) continue;

        const fileName = __file.split('/src/views')[1]?.replace('/index.vue', '') || '';
        const fixName =
          name && name === configPath ? '' : `defineOptions({ name: '${configPath}' });`;

        routePage = { name, fixName, filePath: __file, fileName };
        break;
      }

      if (routePage) {
        Object.assign(this.debugInfo, { routePage });
        const logFn = route.meta.keepAlive ? console.error : warn;
        routePage.fixName && logFn('页面路由地址与页面组件name不一致，会导致KeepAlive无效');
      }
      return this;
    },
    _isConfigExists(pageCode: string) {
      return !!this.debugInfo.configList.find((i) => i.pageCode == pageCode);
    },
    appendRouteConfig(configInfo: PageConfig) {
      !this._isConfigExists(configInfo.pageCode) &&
        this.debugInfo.configList.push(
          pick(configInfo, ['pageCode', 'pageTitle', 'template', 'dataStatus', 'templateList']),
        );

      configInfo.areaDTOList
        .filter((i) => i.linkPageCode)
        .forEach((i) => {
          if (!i.linkPageCode || this._isConfigExists(i.linkPageCode)) return;
          this.debugInfo.configList.push({
            pageCode: i.linkPageCode,
            pageTitle: i.areaName,
            template: i.linkPage?.split('@')[0],
            parentCode: configInfo.pageCode,
          });
        });
      return this;
    },
    resetRouteCache() {
      this.debugInfo = { routePage: {}, configList: [] };
    },

    setStationMode(mode: StationMode) {
      this.stationMode = mode;
      $MTS.App.prop('STATION_MODE', mode);
      $MTS.App.setItem('STATION_MODE', mode);
    },
    async loadStationInfo(inLogin = false) {
      const promises: Promise<void>[] = [];

      promises.push(
        getWorkStationInfo().then((res) => {
          if (!res) return;
          this.setUseRecord(StationMode.WK_STA, res);
        }),
      );

      if (!inLogin) {
        promises.push(
          getSortStationInfo().then((res = {}) => {
            res.stationNo = res.stationNo || localStorage.getItem('sortation:stationNo');
            if (!res.stationNo) return;

            localStorage.setItem('sortation:stationNo', res.stationNo);
            this.setUseRecord(StationMode.SST, res);
          }),
        );
      }

      promises.push(
        (this.stationMode === StationMode.YYBD ? getSampleStationInfo() : getStationInfo()).then(
          (res) => this.setStationInfo({ ...res }),
        ),
      );

      await Promise.all(promises);
    },
    async updateSortStationInfo(params: Recordable) {
      await updateSortStationInfo(params);
      localStorage.setItem('sortation:stationNo', params.stationNo);
      this.setUseRecord(StationMode.SST, params);
    },
    async updateStationInfo(params: Recordable) {
      if (this.stationMode === StationMode.YYBD) {
        const user = getUserStore().getUserInfo;
        const res = await updateSampleStationInfo({
          loginUserId: user.id,
          loginUserNickname: user.nickname,
          factoryNo: this.getSampleFactoryNo,
          ...params,
        });
        Object.assign(params, res);
      } else {
        const res = await updateStationInfo(params);
        Object.assign(params, res);
      }
      this.setStationInfo(params);
    },
    /** 分身绑定站位：重复绑定即改绑，绑定结果本地持久化用于展示 */
    async updateAvatarStationInfo(params: Recordable) {
      await bindAvatarStation(params as { lineNo: string; stationNo: string });
      this.setUseRecord(StationMode.AVATAR_STA, pick(params, ['lineNo', 'stationNo']));
    },
    /** 解除分身绑定 */
    async unbindAvatarStationInfo() {
      await unbindAvatarStation();
      this.setUseRecord(StationMode.AVATAR_STA, {});
    },
    setStationInfo(stationInfo: Recordable) {
      const { lineNo, stationNo, factoryNo = this.getFactoryNo } = stationInfo;
      const WS = useWS();

      if (!lineNo || !stationNo || !factoryNo) {
        WS.status.value === 'OPEN' && WS.close();
        return;
      }

      const newStationKey = [lineNo, stationNo, factoryNo].join('_');
      if (newStationKey !== this.getStationKey) {
        this.setUseRecord(this.stationMode, { lineNo, stationNo, factoryNo });
        WS.status.value === 'OPEN' && WS.close();
      }
      WS.connect();
    },
    getUseRecord(key: string) {
      return this.useRecord[key];
    },
    setUseRecord(key: string, value: any) {
      this.useRecord[key] = value;
      return this;
    },
    toggleDevMode(mode?: boolean) {
      this.setUseRecord('developerMode', mode ?? !this.getDeveloperMode);
      getPermissionStore().setLastBuildMenuTime();
    },
    clearLocalCache() {
      this.resetRouteCache();
      this.useRecord = {};
      this.systemConfg = {};
      this.userConfg = {};
      Object.values(this.fetchTimers).forEach((i) => i.stop());
      this.detector.destroy?.();
    },

    mergeConfigList(list: SysConfig[] = [], type: SysConfigType = 'user') {
      const configKey = type + 'Confg';
      const newConfig = list.reduce((acc, cur) => {
        acc[cur.configKey] = parseConfigValue(cur);
        return acc;
      }, {} as Recordable);
      this[configKey] = { ...this[configKey], ...newConfig };
    },
    async loadAllSysConfig() {
      this.mergeConfigList(await API.getSysConfigList({}, 'system'), 'system');
      this.sewingConfig = this.getConfig['suspension:basic_configuration'] || {};
    },
    async loadDefUserConfig() {
      this.mergeConfigList(await API.getSysConfigList());
    },
    async initGlobalConfig() {
      const promises: Promise<void>[] = [];

      promises.push(this.loadAllSysConfig());
      promises.push(this.loadDefUserConfig());
      if (PADMode) {
        promises.push(this.loadStationInfo());
      }
      await Promise.allSettled(promises);

      const initEvent = () => {
        !IN_DEV && this.initSysConfigFetcher();
        PADMode && this.initRestartChecker();
        !PADMode && this.initInteractionDetector();
      };
      initEvent();

      $MTS.App.onResume(initEvent);
      document.addEventListener('visibilitychange', () => {
        !document.hidden && initEvent();
      });
    },
    async updateConfig(params: SysConfig, type: SysConfigType = 'user') {
      const res = await API.saveSysConfig(params, type);
      res && (this[type + 'Confg'][res.configKey] = parseConfigValue(res));
    },
    async loadSewingConfig() {
      const newConfig = await createApi(
        '/mes-production-sewing/sewingConfiguration/basic/query',
        'GET',
      )();
      if (newConfig && JSON.stringify(newConfig) !== JSON.stringify(this.sewingConfig)) {
        this.sewingConfig = newConfig;
      }
    },
    initSysConfigFetcher() {
      const configKey = 'production:report_mode';
      this.fetchTimers[configKey]?.stop();
      this.fetchTimers[configKey] = recursiveTimeout(() => {
        if (document.hidden) return;

        API.getSysConfig({ configKey }, 'system')
          .then(parseConfigValue)
          .then((res) => {
            if (res === this.getConfig[configKey]) return;
            message.loading(t('g.25090001'), 10).then($MTS.App.restart);
          });
      }, 20 * 1e3);
    },
    initRestartChecker() {
      const configKey = 'pad:restart_system';
      this.fetchTimers[configKey]?.stop();
      const checker = createDayTimeChecker(this.getConfig[configKey]);
      if (!checker) return;

      // 每小时检测，系统运行到第二天后重载系统
      this.fetchTimers[configKey] = recursiveTimeout(
        () => checker() && $MTS.App.restart(),
        60 * 60 * 1e3,
      );
    },
    /** 页面有交互时，1分钟更新一次token过期时间 */
    initInteractionDetector() {
      this.detector?.destroy?.();
      this.detector = new UserInteractionDetector(() => AUTH_API.resetTokenTime());
      window.addEventListener('beforeunload', () => this.detector.destroy());
    },
  },
});
